import type { AllowedDocumentContentType } from '@libs/types';

import { apiClient } from '@api/client';

/**
 * Create presigned upload URL for chat document
 *
 * @param conversationId - Conversation ID
 * @param fileName - File name with extension
 * @param contentType - MIME type of the file
 * @param signal - Optional AbortSignal for cancellation
 * @returns Presigned S3 upload URL, S3 key, and document ID
 */
export const createChatDocumentUploadUrl = async (
  conversationId: string,
  fileName: string,
  contentType: AllowedDocumentContentType,
  signal?: AbortSignal,
): Promise<{ s3UploadUrl: string; s3Key: string; documentId: string }> => {
  const response = await apiClient.post(
    `api/chat/conversations/${conversationId}/documents/create-upload-url`,
    {
      fileName,
      contentType,
    },
    {
      signal,
    },
  );

  return response.data;
};

/**
 * Get presigned download URL for chat document
 *
 * @param documentId - Document ID
 * @param conversationId - Conversation ID
 * @param fileExtension - File extension (without dot)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Presigned S3 download URL
 */
export const getChatDocumentDownloadUrl = async (
  documentId: string,
  conversationId: string,
  fileExtension: string,
  signal?: AbortSignal,
): Promise<{ s3DownloadUrl: string }> => {
  const response = await apiClient.get(
    `api/chat/documents/${documentId}/download-url`,
    {
      params: {
        conversationId,
        fileExtension,
      },
      signal,
    },
  );

  return response.data;
};

/**
 * Delete chat document from S3
 *
 * @param documentId - Document ID
 * @param conversationId - Conversation ID
 * @param fileExtension - File extension (without dot)
 * @param signal - Optional AbortSignal for cancellation
 */
export const deleteChatDocument = async (
  documentId: string,
  conversationId: string,
  fileExtension: string,
  signal?: AbortSignal,
): Promise<void> => {
  await apiClient.delete(`api/chat/documents/${documentId}`, {
    params: {
      conversationId,
      fileExtension,
    },
    signal,
  });
};
